/**
 * Web Firebase Analytics facade.
 *
 * Same exported shape as the native `analytics.ts`. Metro resolves this
 * `.web.ts` ahead of it on web builds, so callsites stay platform-agnostic.
 *
 * GA4 web is only enabled for the production channel (staging/dev would
 * otherwise pollute the same GA4 property). `isSupported()` is also checked
 * to stay silent where GA4 can't run (e.g. ad-blocked browsers).
 */
import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled, setUserId, setUserProperties } from "firebase/analytics"

import { app } from "./firebase.web"
import { ENV } from "./env"
import type { AnalyticsEvent } from "./analytics"

let instance: ReturnType<typeof getAnalytics> | null = null
let initialized = false

/** Lazily create the GA4 instance; null when GA4 shouldn't run. */
async function getInstance() {
  if (initialized) return instance
  initialized = true

  if (ENV.channel !== "production") return null
  if (!(await isSupported().catch(() => false))) return null

  try {
    instance = getAnalytics(app)
  } catch {
    instance = null
  }
  return instance
}

export const analytics = {
  async logEvent(event: AnalyticsEvent) {
    const a = await getInstance()
    if (!a) return
    // GA4 has typed overloads for reserved event names (`login`, `screen_view`, ...).
    // Route everything through the string-name overload so our typed catalog maps 1:1.
    const params = "params" in event ? event.params : undefined
    logEvent(a, event.name as string, params as Record<string, unknown>)
  },
  async logScreenView(screen: { name: string; class?: string }) {
    const a = await getInstance()
    if (!a) return
    logEvent(a, "screen_view", { firebase_screen: screen.name, firebase_screen_class: screen.class })
  },
  async setUserId(id: string | null) {
    const a = await getInstance()
    if (!a) return
    setUserId(a, id)
  },
  async setUserProperties(properties: Record<string, string | null>) {
    const a = await getInstance()
    if (!a) return
    setUserProperties(a, properties)
  },
  async setEnabled(enabled: boolean) {
    const a = await getInstance()
    if (!a) return
    setAnalyticsCollectionEnabled(a, enabled)
  },
}
